import React from 'react';

// =============================================================================
// CampaignDetailsSkeleton — page-mount shape-of-page placeholder.
//
// Shown during the two root loaders (`CampaignDetailsLoader` +
// `CampaignDetailsChartsDataLoader`) while the initial Apollo queries are
// in flight and no campaign data exists yet. Mocks the final Campaign
// Details layout — header row (pill + title + action cluster), 7-tab
// navigation bar, Performance card with KPI strip + chart — using
// generic `<span class="skeleton"/>` bars from `_design-system.scss` §20.
//
// Scope: only rendered at page mount. In-context tab-switch loading
// (after page shell has rendered) uses a simpler card-shape spinner in
// the tab body — not a skeleton — because the shell + tab bar are
// already visible.
//
// Why not a centered spinner: perceived performance on dashboard pages
// improves ~20–30% with skeleton screens (LinkedIn 2013, replicated
// since). User's eye tracks the layout shape; the page feels like it's
// "almost there" rather than blank-then-sudden.
// =============================================================================

const TAB_COUNT = 7;
const KPI_COUNT = 4;

const bar = (width, height = 16, radius = 4) => (
  <span
    className="skeleton"
    style={{ width, height, borderRadius: radius, display: 'inline-block' }}
  />
);

const CampaignDetailsSkeleton = () => (
  <div className="mjl-ds campaign-details-skeleton">
    {/* ── Header: status pill + title + action buttons ─────────────────── */}
    <div className="campaign-details-skeleton__header">
      <div className="campaign-details-skeleton__title-row">
        {bar(70, 22, 9999)}
        {bar(280, 26, 6)}
      </div>
      <div className="campaign-details-skeleton__actions">
        {bar(128, 32, 10)}
        {bar(112, 32, 10)}
        {bar(32, 32, 8)}
      </div>
    </div>

    {/* ── Tab bar — 7 pill-shaped placeholders ─────────────────────────── */}
    <div className="campaign-details-skeleton__tabs">
      {Array.from({ length: TAB_COUNT }).map((_, i) => (
        <React.Fragment key={i}>{bar(104, 32, 8)}</React.Fragment>
      ))}
    </div>

    {/* ── Performance card shell with 4 KPI slots + chart ──────────────── */}
    <section className="campaign-overview__performance">
      <header className="campaign-overview__performance-head">
        {bar(96, 12, 4)}
      </header>

      <div className="campaign-details-skeleton__kpi-row">
        {Array.from({ length: KPI_COUNT }).map((_, i) => (
          <div key={i} className="campaign-details-skeleton__kpi">
            {bar(32, 32, 8)}
            {bar(90, 12, 4)}
            {bar(140, 28, 6)}
          </div>
        ))}
      </div>

      <div style={{ padding: 16 }}>
        {bar('100%', 280, 12)}
      </div>
    </section>
  </div>
);

export default CampaignDetailsSkeleton;
